import { checkReadyToBid } from '@/components/GridItemCard/Card/helpers/checkReadyToBid';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import {
    getCatalogNetworkStatus,
    getLatestMessageTimestamp,
    getLiveCatalogClosed,
    getLiveCatalogCurrentItemId,
    getLiveCatalogCurrentItemIndex,
    getLiveCatalogStatus,
    getLiveCatalogWon,
    getLiveItemDataCurrentAskPrice,
} from '@/redux/modules/console';
import {
    getItemIndex,
    getItemModelCatalogId,
    getItemModelCatalogStatus,
    getItemModelSalePrice,
    getItemModelStartPrice,
    getItemSummaryLeadingBid,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
    getRegistrationBidLimit,
    getRegistrationBlocked,
    getRegistrationDeclined,
    getRegistrationSuspended,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getUserIsInGoodStanding } from '@/redux/modules/account/user/user.selectors';
import { isMyBidsPage, isSavedItemsPage } from '@/utils/urls';
import { ItemCardComponent, ItemCardInfo } from '@/components/GridItemCard/types/types';
import { ItemCardSource, useTrackItemCardClicked } from '@/utils/amplitude/analytics';
import { useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import CardGridBody from './components/CardGridBody/CardGridBody';
import CardImage from './components/CardImage/CardImage';
import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import theme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import useIsWiderThan from '@/hooks/useIsWiderThan';

const MyItemsUpcomingItemCard: ItemCardComponent = ({ className, itemId }) => {
    const trackItemCardClicked = useTrackItemCardClicked();

    const { pathname } = useLocation();

    const isMobile = !useIsWiderThan(theme.breakpoints.tabletNarrowWidthNum);

    const [cardHovered, setCardHovered] = useState(false);
    const [linkHovered, setLinkHovered] = useState(false);

    const accountInGoodStanding = useAppSelector(getUserIsInGoodStanding);
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemModelCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemIndex(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const salePrice = useAppSelector((state) => getItemModelSalePrice(state, itemId));
    const startPrice = useAppSelector((state) => getItemModelStartPrice(state, itemId));

    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const bidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));
    const blockedByAuctioneer = useAppSelector((state) => getRegistrationBlocked(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));
    const limited = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const suspended = useAppSelector((state) => getRegistrationSuspended(state, catalogId));

    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const closed = useAppSelector((state) => getLiveCatalogClosed(state, catalogId));
    const won = useAppSelector((state) => getLiveCatalogWon(state, catalogId));
    const status = useAppSelector((state) => getLiveCatalogStatus(state, catalogId));
    const currentItem = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const liveUpdateTimestamp = useAppSelector((state) => getLatestMessageTimestamp(state, catalogId));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const networkStatus = useAppSelector((state) => getCatalogNetworkStatus(state, catalogId));

    const currentPrice = leadingBid || salePrice || startPrice || 0;
    const overBidLimit = limited ? bidLimitBalance + currentPrice > bidLimit : false;

    const comingUp = catalogStatus === 'live' && currentItemIndex <= index;
    const currentItemLive = comingUp && currentItemIndex === index;
    const isDone = catalogStatus === 'done';
    const showBidStatus = !isDone;

    const isStoppedFromBidding =
        !accountInGoodStanding ||
        (!approved && !limited) ||
        suspended ||
        declined ||
        blockedByAuctioneer ||
        overBidLimit ||
        !checkReadyToBid({
            catalogId,
            closed,
            currentAskPrice,
            currentItem,
            itemId,
            liveUpdateTimestamp,
            networkStatus,
            status,
            won,
        });

    const onCardClick = useCallback(() => {
        if (isSavedItemsPage(pathname)) {
            trackItemCardClicked({ itemId, source: ItemCardSource.MyItemsPageFavorites });
        } else if (isMyBidsPage(pathname)) {
            trackItemCardClicked({ itemId, source: ItemCardSource.MyItemsPageBids });
        }
    }, [pathname, itemId, trackItemCardClicked]);

    const enterHoverState = useCallback(() => {
        setCardHovered(true);
    }, []);

    const leaveHoverState = useCallback(() => {
        setCardHovered(false);
    }, []);

    const enterLinkHoverState = useCallback(() => {
        setLinkHovered(true);
    }, []);

    const leaveLinkHoverState = useCallback(() => {
        setLinkHovered(false);
    }, []);

    return (
        <StyledCard
            data-testid={`saved-item-card-${itemId}`}
            onClick={onCardClick}
            onMouseEnter={enterHoverState}
            onMouseLeave={leaveHoverState}
        >
            <SavedItemsCardGrid
                $auctionEnded={isDone}
                $itemLive={currentItemLive}
                className={className}
                data-testid="saved-items-card-grid"
            >
                <CardImage
                    enterLinkHoverState={enterLinkHoverState}
                    isMobile={isMobile}
                    itemId={itemId}
                    leaveLinkHoverState={leaveLinkHoverState}
                    showBidStatus={showBidStatus}
                />
                <CardGridBody
                    cardHovered={cardHovered}
                    enterLinkHoverState={enterLinkHoverState}
                    isCurrentLiveitem={currentItemLive}
                    isMobile={isMobile}
                    isStoppedFromBidding={isStoppedFromBidding}
                    itemId={itemId}
                    leaveLinkHoverState={leaveLinkHoverState}
                    linkHovered={linkHovered}
                />
            </SavedItemsCardGrid>
        </StyledCard>
    );
};

const info: ItemCardInfo = {
    ItemCardComponent: MyItemsUpcomingItemCard,
};

export default info;

const StyledCard = styled.article`
    position: relative;
`;

const SavedItemsCardGrid = styled.section<{
    $auctionEnded: boolean;
    $itemLive: boolean;
}>`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: min-content min-content min-content;
    grid-template-areas:
        'itemimage'
        '.'
        'alldetailsformobilelayout';
    justify-content: center;
    ${({ $itemLive }) =>
        $itemLive &&
        css`
            background-color: ${({ theme }) => theme.colors.grey500};

            @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
                background-color: unset;
                margin: unset;
                padding: unset;
            }
        `}

    @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
        grid-template-areas: 'itemimage alldetailsformobilelayout';
        grid-template-columns: 1fr 4fr;
        grid-template-rows: min-content min-content;
        column-gap: 16px;
        ${({ $auctionEnded }) =>
            $auctionEnded &&
            css`
                grid-template-rows: min-content 11px min-content 4px min-content 2px min-content 0;
            `}
    }
`;
